import { GALLERY_SORTS, GALLERY_VIEWS, GALLERY_FILTERS, GALLERY_PREVIEW_SIZES, type GalleryPreviewSize, type GallerySort, type GalleryView, type GalleryStatusFilter } from '../../../features/takeoff/utils/galleryOrganisation';
/** Header-action builder for the Takeoff canvas tab.
 *  Owns: Upload sheet, mobile Run AI, and Export.
 *  (Summary lives in the side tool rail — see TakeoffRightPanel. Drawing
 *  scale and display settings — isolate/invert/minimap — moved to the
 *  bottom canvas cluster, see TakeoffPreviewCanvas.tsx's bottomScaleAction /
 *  bottomSettingsAction, so they aren't duplicated between toolbar and canvas.)
 *  Canvas state still distinguishes the drawing from the gallery, but fit and
 *  zoom now live together in the bottom canvas cluster. No JSX. */

import {
  Plus,
  ArrowUpDown,
  LayoutGrid,
  List,
  Filter,
  Upload,
  Sparkles,
  Download,
  Loader2,
  Check,
  SquarePen,
  Trash2,
  MoreHorizontal,
} from '@/shared/ui/icons';
import { SidebarRightIcon } from '../../../shared/ui/SidebarLeftIcon';
import type { MobileHeaderAction, MobileHeaderSubAction } from './types';
import { REGION_FIELDS, type RegionField } from '../../../features/takeoff/sheets/TitleBlockRegionEditor';
import { primaryModifierKeyLabel } from '../../../utils/keyboardModifier';

/** Takeoff inspector state the header's Types control mirrors. Broadcast by
 *  TakeoffView via `takeoff:panel-state-change`. */
export interface TakeoffPanelState {
  section: 'sheets' | 'types' | 'ai';
  collapsed: boolean;
  /** AI suggestions awaiting review on the open drawing. The review tray sits
   *  in the Types inspector, which a phone keeps closed over the drawing, so
   *  the count rides on the control that opens it. */
  pendingSuggestionCount?: number;
}

export interface TakeoffHeaderState {
  gallery?: { sort: GallerySort; view: GalleryView; status: GalleryStatusFilter; previewSize?: GalleryPreviewSize };
  canUpload: boolean;
  canRunAI: boolean;
  isAILoading: boolean;
  canExport: boolean;
  isExporting: boolean;
  summaryOpen: boolean;
  canAISuggest: boolean;
  aiSuggestActive: boolean;
  /** Drawing-scale pill state. `scaleLabel` is the persisted calibration
   *  label (e.g. "1 : 250"); null until the sheet is calibrated. */
  scaleLabel: string | null;
  calibrated: boolean;
  /** Auto-detected scale from the PDF text layer, surfaced at the top of the
   *  scale menu for one-click apply. Null once calibrated. */
  scaleSuggestion: { denom: number; source: 'title-block' | 'page' } | null;
  /** True while the full-page Add Drawings uploader is open. The global
   *  header's back chevron closes it (→ sheet gallery), so the uploader
   *  needs no back control of its own. */
  uploaderOpen?: boolean;
  /** At least one sheet exists in the project — gates the "Sheet details"
   *  after-the-fact editor pill, which is meaningless on an empty gallery. */
  hasSheets?: boolean;
  /** True while the after-the-fact Sheet details editor (TakeoffSheetDetailsEditor)
   *  is open. Hides its own entry-point pill so it can't be re-opened onto itself. */
  sheetDetailsOpen?: boolean;
  /** Gallery multi-select — count of sheets currently selected (already
   *  pruned to sheets that still exist, see resolveSelectedSheets) and their
   *  ids, so "Edit details" can open TakeoffSheetDetailsEditor scoped to just
   *  this subset. 0/[] when nothing is selected. */
  selectedSheetCount?: number;
  selectedSheetIds?: string[];
  /** Count of sheets currently on screen under the gallery's search + status
   *  tab (see TakeoffSheetGalleryGrid's resolveVisibleSheets) — what "Select
   *  all" would select, and what decides whether that pill is offered at all. */
  visibleSheetCount?: number;
  selectedVisibleSheetCount?: number;
  /** How many drawings the gallery's Download action would actually build:
   *  the selection when there is one, else everything visible, narrowed in
   *  both cases to sheets that carry measurements (see resolveDownloadSheets).
   *  0 hides the pill — an unmeasured gallery has no takeoff to download. */
  downloadableSheetCount?: number;
  /** True while a bulk download is building, so the pill reads as busy
   *  instead of starting a second run over the same drawings. */
  isDownloading?: boolean;
}

/** A gallery-only morph must not take over the mobile header on a drawing. */
export function isTakeoffGallerySearchAvailable(
  isTakeoffTab: boolean,
  headerState: Pick<TakeoffHeaderState, 'uploaderOpen' | 'sheetDetailsOpen'> | null,
  drawingOpen: boolean,
): boolean {
  return isTakeoffTab
    && !!headerState
    && !drawingOpen
    && !headerState.uploaderOpen
    && !headerState.sheetDetailsOpen;
}

/** Details-step (title-block region drawing) upload-nav payload — see
 *  TakeoffHeaderCtx.takeoffUploaderDetails. */
export interface TakeoffUploaderDetails {
  step: string;
  activeField: RegionField;
  fieldsSet: Record<RegionField, boolean>;
  sheetCount: number;
}

export interface TakeoffCanvasState {
  sheetName: string | null;
  zoomPct: number;
  vectorViewing: boolean;
  invertSheet: boolean;
  showMinimap: boolean;
  isIsolated: boolean;
  canIsolate: boolean;
}

export interface TakeoffHeaderCtx {
  takeoffHeaderState: TakeoffHeaderState;
  takeoffCanvasState: TakeoffCanvasState | null;
  /** Left-panel state — drives the leading "Panel" section dropdown. Null
   *  until the panel mounts and broadcasts. */
  takeoffPanelState: TakeoffPanelState | null;
  /** Number of drawings the uploader currently has selected (0 when none, when
   *  the uploader is closed, or once processing has started — the uploader
   *  reports 0 off the source step). >0 surfaces the Continue / Clear actions. */
  takeoffUploaderSelected?: number;
  /** True only while the uploader is on its source (file-picking) step —
   *  gates the Browse action, since picking more files mid-import is wrong. */
  takeoffUploaderCanBrowse?: boolean;
  /** Details-step state (title-block region drawing) — which field a drawn
   *  box currently assigns to, which fields already have a box, and how many
   *  sheets will be imported (net of rows the user has excluded). Null outside
   *  the details step / uploader. Broadcast by TakeoffUploader on
   *  takeoff:upload-nav. */
  takeoffUploaderDetails?: TakeoffUploaderDetails | null;
  /** Mutates in-place; caller owns the arrays. */
  leadingActions: MobileHeaderAction[];
  trailingActions: MobileHeaderAction[];
}

/** Appends Takeoff pills to the caller's leading/trailing action arrays.
 *  Called only when `takeoffHeaderState` is non-null. */
export function buildTakeoffHeaderActions(ctx: TakeoffHeaderCtx): void {
  const { takeoffHeaderState, takeoffCanvasState, takeoffPanelState, trailingActions } = ctx;
  const uploaderSelected = ctx.takeoffUploaderSelected ?? 0;
  const details = ctx.takeoffUploaderDetails;

  if (takeoffHeaderState.uploaderOpen && details?.step === 'details') {
    // Details step (title-block region drawing) — the field toggles + sheet
    // count move up here from above the canvas so the drawing gets the
    // vertical space. Each toggle sets which box a drag assigns to; a check
    // shows once that field has a drawn box. Import runs the same confirm
    // the footer's Import button does.
    for (const f of REGION_FIELDS) {
      trailingActions.push({
        id: `takeoff-upload-field-${f.key}`,
        label: f.label,
        icon: details.fieldsSet[f.key] ? Check : undefined,
        group: 'create',
        active: details.activeField === f.key,
        ariaLabel: `Draw the ${f.label.toLowerCase()} box`,
        onClick: () => window.dispatchEvent(new CustomEvent('takeoff:upload-field', { detail: { field: f.key } })),
      });
    }
    // Cancel is a FULL exit, not a step back (the chevron owns that, see
    // takeoff:upload-back-to-source). It lives here because the review grid is
    // the last step before import and, with the page picker retired, this is
    // the only one-press way out of a parsed batch — otherwise leaving means
    // the chevron back to the source step and then out again.
    trailingActions.push({
      id: 'takeoff-upload-cancel',
      label: 'Cancel',
      group: 'create',
      ariaLabel: 'Cancel and close the uploader',
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:upload-close')),
    });
    trailingActions.push({
      id: 'takeoff-upload-import',
      label: `Import ${details.sheetCount} sheet${details.sheetCount === 1 ? '' : 's'}`,
      group: 'create',
      ariaLabel: `Import ${details.sheetCount} sheet${details.sheetCount === 1 ? '' : 's'}`,
      disabled: details.sheetCount === 0,
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:upload-import')),
    });
  } else if (takeoffHeaderState.uploaderOpen) {
    // Uploader already open — the "+" (open uploader) would be a no-op, so
    // the slot becomes the "Browse my computer" file-picker trigger. Moved
    // here from the uploader's bottom dropzone so it stops eating the page.
    // Source step only (canBrowse) — the later steps own the queued batch.
    if (ctx.takeoffUploaderCanBrowse) {
      trailingActions.push({
        id: 'takeoff-upload-browse',
        icon: Upload,
        group: 'create',
        ariaLabel: 'Browse my computer',
        onClick: () => window.dispatchEvent(new CustomEvent('takeoff:upload-browse')),
      });
    }
    // Selection actions — Clear + the primary "Continue with N drawings",
    // moved here from the uploader's bottom footer. Only while something's
    // selected.
    if (uploaderSelected > 0) {
      trailingActions.push({
        id: 'takeoff-upload-clear',
        label: 'Clear',
        group: 'create',
        ariaLabel: 'Clear selected drawings',
        onClick: () => window.dispatchEvent(new CustomEvent('takeoff:upload-clear')),
      });
      trailingActions.push({
        id: 'takeoff-upload-continue',
        label: `Continue with ${uploaderSelected} drawing${uploaderSelected === 1 ? '' : 's'}`,
        group: 'create',
        ariaLabel: `Continue with ${uploaderSelected} selected drawing${uploaderSelected === 1 ? '' : 's'}`,
        onClick: () => window.dispatchEvent(new CustomEvent('takeoff:upload-continue')),
      });
    }
  } else if (takeoffHeaderState.canUpload && !takeoffCanvasState && !takeoffHeaderState.sheetDetailsOpen) {
    // Suppressed while the Sheet details editor is open: its own onUpload
    // listener unmounts the editor unconditionally, which would silently
    // discard unsaved title-block edits (P2 review finding on PR #723).
    // Hiding the pill removes the only route to that handler from the
    // gallery state - the editor's own Back/Save are the way out.
    trailingActions.push({
      id: 'takeoff-upload',
      icon: Plus,
      group: 'create',
      ariaLabel: 'Upload sheet',
      onClick: (event) => window.dispatchEvent(new CustomEvent('takeoff:upload', {
        detail: { trigger: event?.currentTarget ?? null },
      })),
    });
  }

  // Gallery multi-select cluster — count + Edit details/Delete/Select all/
  // Clear. Same gallery-visible gate as "Sheet details" below (no drawing
  // open, uploader closed, editor not already open), plus an active
  // selection. Takes over the slot the plain "Sheet details" pill would
  // occupy: editing/deleting a hand-picked subset reads oddly next to "edit
  // every sheet", so the two never show together.
  const selectedSheetCount = takeoffHeaderState.selectedSheetCount ?? 0;
  const visibleSheetCount = takeoffHeaderState.visibleSheetCount ?? 0;
  const galleryVisible =
    !takeoffHeaderState.uploaderOpen && !takeoffCanvasState && !takeoffHeaderState.sheetDetailsOpen;
  if (galleryVisible && takeoffHeaderState.gallery) {
    const state = takeoffHeaderState.gallery;
    for (const [field, label, icon, options] of [
      ['sort', 'Sort drawings', ArrowUpDown, GALLERY_SORTS],
      ['view', 'Drawing view', state.view === 'grid' ? LayoutGrid : List, GALLERY_VIEWS],
      ['status', 'Filter drawings', Filter, GALLERY_FILTERS],
    ] as const) {
      const subActions: MobileHeaderSubAction[] = options.map(([value, text]) => ({
        id: `takeoff-gallery-${field}-${value}`, label: text, active: state[field] === value,
        trailingIcon: state[field] === value ? Check : undefined,
        onClick: () => window.dispatchEvent(new CustomEvent('takeoff:gallery-change', { detail: { field, value } })),
      }));
      if (field === 'view') {
        subActions.push(...GALLERY_PREVIEW_SIZES.map(([value, text]) => ({
          id: `takeoff-gallery-previewSize-${value}`, label: `${text} previews`,
          active: (state.previewSize ?? 'medium') === value, disabled: state.view !== 'grid',
          trailingIcon: (state.previewSize ?? 'medium') === value ? Check : undefined,
          onClick: () => window.dispatchEvent(new CustomEvent('takeoff:gallery-change', { detail: { field: 'previewSize', value } })),
        })));
      }
      trailingActions.push({
        id: `takeoff-gallery-${field}`, icon, ariaLabel: label, group: 'view',
        label: field === 'status' && state.status !== 'all' ? options.find(([value]) => value === state.status)?.[1] : undefined,
        badge: field === 'status' && state.status !== 'all' ? 1 : undefined,
        subActions, subActionDividerAfter: field === 'view' ? 'takeoff-gallery-view-list' : undefined,
      });
    }
  }
  const gallerySelectionActive = galleryVisible && selectedSheetCount > 0;
  // Offered whenever picking it would still change something — including
  // before the user has selected anything, so they aren't forced to
  // hand-pick a first sheet just to unlock it — and hidden the moment every
  // visible sheet already is selected, so it and Clear never both claim the
  // same state.
  const selectedVisibleSheetCount = takeoffHeaderState.selectedVisibleSheetCount ?? selectedSheetCount;
  const canSelectAll = galleryVisible && visibleSheetCount > 1 && selectedVisibleSheetCount < visibleSheetCount;
  const selectAllAction: MobileHeaderAction = {
    id: 'takeoff-selection-select-all',
    label: 'Select all',
    group: 'modify',
    // Names the platform's own modifier (⌘ on Apple, Ctrl on Windows/Linux/
    // Android web) — the Cmd/Ctrl+A handler itself already accepts either.
    ariaLabel: `Select all ${visibleSheetCount} visible drawings (${primaryModifierKeyLabel()}+A)`,
    onClick: () => window.dispatchEvent(new CustomEvent('takeoff:selection-select-all')),
  };
  // Export only drawings carrying measurements, scoped to the selection or filter.
  const downloadCount = takeoffHeaderState.downloadableSheetCount ?? 0;
  const isDownloading = !!takeoffHeaderState.isDownloading;
  const canDownloadSheets = galleryVisible && downloadCount > 0;
  const downloadAction = (selectionScoped: boolean): MobileHeaderAction => ({
    id: 'takeoff-selection-download',
    icon: isDownloading ? Loader2 : Download,
    label: selectionScoped ? undefined : 'Download measured drawings',
    group: 'modify',
    disabled: isDownloading,
    ariaLabel: isDownloading
      ? 'Building takeoff download'
      : selectionScoped
        ? `Download ${downloadCount} selected measured drawing${downloadCount === 1 ? '' : 's'} as a zip`
        : `Download ${downloadCount} measured drawing${downloadCount === 1 ? '' : 's'} as a zip`,
    onClick: () => window.dispatchEvent(new CustomEvent('takeoff:selection-download')),
  });
  if (gallerySelectionActive) {
    trailingActions.push({
      // No onClick and NOT disabled — an informational chip, not a dead
      // control. The header renders disabled pills at opacity-40, which read
      // as broken chrome for a chip that's meant to just state a fact.
      id: 'takeoff-selection-count',
      label: `${selectedSheetCount} selected`,
      ariaLabel: `${selectedSheetCount} sheet${selectedSheetCount === 1 ? '' : 's'} selected`,
      group: 'modify',
    });
    trailingActions.push({
      id: 'takeoff-selection-edit-details',
      icon: SquarePen,
      label: 'Edit details',
      group: 'modify',
      ariaLabel: `Edit details for ${selectedSheetCount} selected sheet${selectedSheetCount === 1 ? '' : 's'}`,
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:sheet-details', {
        detail: { sheetIds: takeoffHeaderState.selectedSheetIds ?? [] },
      })),
    });
    if (canDownloadSheets) trailingActions.push(downloadAction(true));
    trailingActions.push({
      id: 'takeoff-selection-delete',
      icon: Trash2,
      group: 'modify',
      ariaLabel: `Delete ${selectedSheetCount} selected sheet${selectedSheetCount === 1 ? '' : 's'}`,
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:selection-delete')),
    });
    // Select all sits before Clear so the pair reads left-to-right as "pick
    // everything" then "pick nothing" — never both offered once they'd do
    // the same thing (see canSelectAll).
    if (canSelectAll) trailingActions.push(selectAllAction);
    trailingActions.push({
      id: 'takeoff-selection-clear',
      label: 'Clear',
      group: 'modify',
      ariaLabel: 'Clear sheet selection',
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:selection-clear')),
    });
  } else if (galleryVisible && takeoffHeaderState.hasSheets) {
    // Infrequent gallery-wide actions stay reachable without filling the header.
    const subActions: MobileHeaderSubAction[] = [{
      id: 'takeoff-sheet-details', label: 'Sheet details', icon: SquarePen,
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:sheet-details')),
    }];
    if (canSelectAll) subActions.push({
      id: selectAllAction.id, label: 'Select all', ariaLabel: selectAllAction.ariaLabel,
      onClick: () => selectAllAction.onClick?.(),
    });
    if (canDownloadSheets) {
      const download = downloadAction(false);
      subActions.push({
        id: download.id, label: 'Download measured drawings', ariaLabel: download.ariaLabel,
        sublabel: `${downloadCount} measured drawing${downloadCount === 1 ? '' : 's'} as a zip`,
        icon: download.icon, disabled: download.disabled,
        onClick: () => download.onClick?.(),
      });
    }
    trailingActions.push({
      id: 'takeoff-gallery-actions', icon: MoreHorizontal, ariaLabel: 'Drawing actions',
      group: 'modify', subActions,
    });
  }

  // On desktop QuantisAI is opened from the global header, which hosts the
  // takeoff assistant on a drawing. Compact layouts keep this entry.
  const aiUsesDesktopRail = typeof window !== 'undefined'
    && !!window.matchMedia?.('(min-width: 1024px)').matches;
  if (takeoffHeaderState.canRunAI && !aiUsesDesktopRail) {
    trailingActions.push({
      id: 'takeoff-run-ai',
      group: 'modify',
      icon: takeoffHeaderState.isAILoading ? Loader2 : Sparkles,
      ariaLabel: 'Open QuantisAI',
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:open-ai-inspector')),
    });
  }

  // Display options (isolate / invert / minimap) moved to the bottom canvas
  // cluster's settings cog (TakeoffPreviewCanvas.tsx's bottomSettingsAction) —
  // that's now the "takeoff page settings" home, with room to grow.

  if (takeoffHeaderState.canExport) {
    trailingActions.push({
      id: 'takeoff-export',
      group: 'output',
      icon: takeoffHeaderState.isExporting ? Loader2 : Download,
      ariaLabel: takeoffHeaderState.isExporting ? 'Exporting…' : 'Export to Documents',
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:export')),
    });
  }

  // The desktop drawing has Types in the side rail beside Takeoff AI. Keep the
  // header control on compact/phone layouts, where that rail is not persistent.
  // The native macOS toolbar owns its own inspector control and is unchanged.
  const typesUsesDesktopRail = typeof window !== 'undefined'
    && !!window.matchMedia?.('(min-width: 1024px)').matches;
  if (takeoffPanelState && !typesUsesDesktopRail) {
    const typesVisible = takeoffPanelState.section === 'types' && !takeoffPanelState.collapsed;
    const pending = takeoffPanelState.pendingSuggestionCount ?? 0;
    const typesLabel = typesVisible ? 'Hide Types inspector' : 'Show Types inspector';
    trailingActions.push({
      id: 'takeoff-panel-collapse',
      group: 'inspector',
      icon: SidebarRightIcon,
      ariaLabel: pending > 0
        ? `${typesLabel}, ${pending} AI suggestion${pending === 1 ? '' : 's'} to review`
        : typesLabel,
      onClick: () => window.dispatchEvent(new CustomEvent('takeoff:open-types-inspector')),
      badge: pending > 0 ? pending : undefined,
      badgeTone: 'brand',
    });
  }
}
